import { first, isNil } from 'lodash';
import { createSelector } from 'reselect';
import {
  BANK_STATUS,
  eligibleBankStatusesMap,
  VERIFICATION_STATUS,
} from 'src/settings/settings';
import { Bank } from 'src/types';
import {
  APP_BUY_TRANSACTION,
  APP_SELL_TRANSACTION,
  AUTOMATIC_SELL_TRANSACTION,
  BUY_TRANSACTION,
  COLLEGE_CONTRIBUTE_TRANSACTION,
  COLLEGE_DISTRIBUTE_TRANSACTION,
  RECURRING_BUY_TRANSACTION,
  IRA_AUTOMAX_TRANSACTION,
  RECURRING_BUY_TRANSACTION_EDIT,
  SELL_TRANSACTION,
  transactionType,
} from 'src/utils/transactions-util';
import { isJustAdded } from 'src/utils/utils';
import * as transactionSelectors from '../abstractTransaction/selectors';
import { FundAgreementMap } from '../funds/UserFunds';
import { getAgreementMap } from '../funds/UserFunds/userFunds.selectors';
import { ApplicationState } from '../types';

export const getSlice = ({ existingBanks }: ApplicationState) => existingBanks;
export const getIsLoaded = createSelector(
  [getSlice],
  transactionSelectors.getIsLoaded
);
export const getIsLoading = createSelector(
  [getSlice],
  transactionSelectors.getIsLoading
);
export const getError = createSelector(
  [getSlice],
  transactionSelectors.hasError
);

export const getExistingBanks = ({ existingBanks }: ApplicationState) =>
  existingBanks.existingBanks;

export const isEligible = (bank: Bank, agreementMap: FundAgreementMap) => {
  const mappedBank = agreementMap[bank.bankAgreementID];
  if (mappedBank) {
    // if we are able to match the bank we should check that is eligible to transaction upon
    return (
      mappedBank.associatedAgreements.length > 0 ||
      mappedBank.associableAgreements.length > 0
    );
  } else {
    // if we can't map a bank rely on backend's flag for verified or if is of an eligible status found in settings
    return (
      !bank.bankAwaitingVerification ||
      eligibleBankStatusesMap[bank.verificationStatus]
    );
  }
};

/**
 * getEligibleBanks
 * @param Function Selector for existing banks
 * @param Function Selector for agreement map from userFunds (not used currently in callback but can be provided to do agreement level filtering)
 * CallBack Function
 * filtered - will filter banks by available map ids
 * eligible - will take filtered banks an apply second filter for association and if
 * user has no agreements to be associated or are currently associated filter those banks
 * Why?
 * This was developed due to high-value client whose beneficiaries banks were visible to him. This could
 * also happen via power of attorney or etc
 */

export const getEligibleBanks = createSelector(
  [getExistingBanks, getAgreementMap],
  (existingBanks, agreementMap) =>
    existingBanks
      .filter((bank) => isEligible(bank, agreementMap))
      .map((bank) => {
        const bankCopy: Bank = {
          ...bank,
          bankStatus: null,
        };
        if (
          bank.bankAwaitingVerification &&
          bank.verificationStatus &&
          bank.verificationStatus === VERIFICATION_STATUS.READY_FOR_VALIDATION
        ) {
          bankCopy.bankStatus = BANK_STATUS.VERIFY_MICRO_DEPOSITS;
        }
        if (bank.verificationStatus === VERIFICATION_STATUS.LAPSED) {
          bankCopy.bankStatus = BANK_STATUS.REQUIRES_RENEWAL;
        }
        if (bank.corruptBankData) {
          bankCopy.bankStatus = BANK_STATUS.CORRUPT_BANK_DATA;
        }
        if (
          bank.bankAwaitingVerification &&
          bank.verificationStatus &&
          bank.verificationStatus === VERIFICATION_STATUS.LAPSED
        ) {
          bankCopy.bankStatus = BANK_STATUS.LAPSED;
        }
        //temp for 955 service updates
        if (
          bank.verificationStatus &&
          bank.verificationStatus === VERIFICATION_STATUS.FAILED
        ) {
          bankCopy.bankStatus = BANK_STATUS.REQUIRES_UPDATE_955;
        }
        switch (transactionType()) {
          case COLLEGE_CONTRIBUTE_TRANSACTION:
            if (isJustAdded(bank)) {
              bankCopy.bankStatus = BANK_STATUS.JUST_ADDED;
            }
            if (
              !isJustAdded(bank) &&
              bank.bankAwaitingVerification &&
              bank.verificationStatus &&
              bank.verificationStatus === VERIFICATION_STATUS.INITIATED
            ) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            break;
          case BUY_TRANSACTION:
            if (isJustAdded(bank)) {
              bankCopy.bankStatus = BANK_STATUS.JUST_ADDED;
            }
            if (
              !isJustAdded(bank) &&
              bank.bankAwaitingVerification &&
              bank.verificationStatus &&
              bank.verificationStatus === VERIFICATION_STATUS.INITIATED
            ) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }

            break;
          case RECURRING_BUY_TRANSACTION:
          case IRA_AUTOMAX_TRANSACTION:
          case APP_BUY_TRANSACTION:
            if (isJustAdded(bank) && bank.externallyVerifiedBank) {
              bankCopy.bankStatus = BANK_STATUS.JUST_ADDED;
            }
            if (isJustAdded(bank) && !bank.externallyVerifiedBank) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            if (
              !isJustAdded(bank) &&
              bank.bankAwaitingVerification &&
              bank.verificationStatus &&
              bank.verificationStatus === VERIFICATION_STATUS.INITIATED
            ) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            break;
          case SELL_TRANSACTION:
          case APP_SELL_TRANSACTION:
          case AUTOMATIC_SELL_TRANSACTION:
          case COLLEGE_DISTRIBUTE_TRANSACTION:
            if (!isNil(bank.hasRedemptionHold) && !!bank.hasRedemptionHold) {
              bankCopy.bankStatus = BANK_STATUS.PROCESSING;
            }
            if (
              bank.bankAwaitingVerification &&
              bank.verificationStatus &&
              bank.verificationStatus === VERIFICATION_STATUS.INITIATED
            ) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            break;
          case RECURRING_BUY_TRANSACTION_EDIT:
            if (isJustAdded(bank) && bank.externallyVerifiedBank) {
              bankCopy.bankStatus = BANK_STATUS.JUST_ADDED;
            }
            if (isJustAdded(bank) && !bank.externallyVerifiedBank) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            if (
              !isJustAdded(bank) &&
              bank.bankAwaitingVerification &&
              bank.verificationStatus &&
              bank.verificationStatus === VERIFICATION_STATUS.INITIATED
            ) {
              bankCopy.bankStatus = BANK_STATUS.PREPARING_MICRO_DEPOSITS;
            }
            break;
          default:
            return bankCopy;
        }
        return bankCopy;
      })
);

/**
 * getSingleBankWithVerifyMicroDeposit
 * @desc logic used to determine if next button should be disabled for
 * buy and college flows
 */
export const getSingleBankWithVerifyMicroDeposit = createSelector(
  [getEligibleBanks],
  (eligibleBanks) => {
    const firstBank = first(eligibleBanks);
    return (
      eligibleBanks.length === 1 &&
      firstBank &&
      (firstBank.bankStatus === BANK_STATUS.PREPARING_MICRO_DEPOSITS ||
        firstBank.bankStatus === BANK_STATUS.VERIFY_MICRO_DEPOSITS)
    );
  }
);

export const getSortedEligibleBanks = createSelector(
  getExistingBanks,
  (existingBanks) => {
    return existingBanks.sort((bankA, bankB) => {
      return bankA.bankVerificationDate - bankB.bankVerificationDate;
    });
  }
);

export const getDefaultBankAgreementKey = createSelector(
  getSortedEligibleBanks,
  (sortedEligibleBanks) => {
    const mostRecentBank = first(sortedEligibleBanks);

    const bankAgreementKey = {
      bankAgreementID:
        mostRecentBank && mostRecentBank.bankAgreementID
          ? mostRecentBank.bankAgreementID
          : null,
      routingNumber:
        mostRecentBank && mostRecentBank.routingNumber
          ? mostRecentBank.routingNumber
          : null,
      accountNumber:
        mostRecentBank && mostRecentBank.accountNumber
          ? mostRecentBank.accountNumber
          : null,
      type:
        mostRecentBank && mostRecentBank.bankAccountType
          ? mostRecentBank.bankAccountType
          : null,
    };
    return bankAgreementKey;
  }
);

export const getBanksWithLapsedState = createSelector(
  getEligibleBanks,
  (eligibleBanks) =>
    eligibleBanks.filter(
      (bank) => bank.bankStatus === BANK_STATUS.REQUIRES_RENEWAL
    )
);

export const getIsSingleBankWithDisabledStatus = createSelector(
  getEligibleBanks,
  (eligibleBanks) => {
    const bank = eligibleBanks?.[0];
    return (
      eligibleBanks.length === 1 &&
      (bank.bankStatus === BANK_STATUS.PREPARING_MICRO_DEPOSITS ||
        bank.bankStatus === BANK_STATUS.VERIFY_MICRO_DEPOSITS ||
        bank.bankStatus === BANK_STATUS.REQUIRES_RENEWAL ||
        bank.bankStatus === BANK_STATUS.REQUIRES_UPDATE_955)
    );
  }
);
